// Map canvas for the Hole Detail page.
//
// Shows the zones that belong to one hole — fitted to their bounding box,
// color-coded by turf type, with optional overlays:
//   • IDW rootzone moisture (unique to GreenIQ — sensor+ET fusion)
//   • sensor dots (always on) color-coded by VWC
//
// Deliberately lighter than CourseMapPage: no polygon editing, no satellite
// index dropdown, no hand-trace tools. Read-only diagnostic canvas for supers.

import { useEffect, useRef, useState } from 'react';
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl';
import type { PlacedSensor, Zone } from '@greeniq/shared';
import { ZONE_TYPE_STYLE } from '../lib/zoneStyle.js';
import { heatmapColor, idwGridForZone } from '../lib/idw.js';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN ?? '';

type Overlay = 'idw' | 'none';

interface Props {
  zones: Zone[];
  sensors: PlacedSensor[];
  /** Optional bbox override; otherwise derived from the zone polygons. */
  bbox?: [number, number, number, number];
}

function bboxOfZones(zones: Zone[]): [number, number, number, number] | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const z of zones) {
    const rings = z.polygon.type === 'Polygon' ? z.polygon.coordinates : z.polygon.coordinates.flat();
    for (const ring of rings) {
      for (const [lng, lat] of ring as [number, number][]) {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      }
    }
  }
  if (!Number.isFinite(minLng)) return null;
  return [minLng, minLat, maxLng, maxLat];
}

function vwcDotColor(vwc: number | null): string {
  if (vwc == null) return '#6b7280';
  if (vwc < 0.10) return '#dc2626';
  if (vwc < 0.13) return '#f59e0b';
  if (vwc > 0.35) return '#2563eb';
  return '#16a34a';
}

export function HoleMap({ zones, sensors, bbox: bboxOverride }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [overlay, setOverlay] = useState<Overlay>('idw');
  const [styleId, setStyleId] = useState<'satellite-streets-v12' | 'satellite-v9'>('satellite-streets-v12');
  const [ready, setReady] = useState(false);

  // Mount map once.
  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN || mapRef.current) return;
    const bbox = bboxOverride ?? bboxOfZones(zones);
    if (!bbox) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: `mapbox://styles/mapbox/${styleId}`,
      bounds: [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      fitBoundsOptions: { padding: 60, duration: 0 },
    });
    mapRef.current = map;
    map.on('load', () => setReady(true));
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
    // Only once on mount — bbox/zones changes are handled by re-render effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap basemap style without tearing down the whole map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(`mapbox://styles/mapbox/${styleId}`);
    map.once('style.load', () => setReady(true));
    setReady(false);
  }, [styleId]);

  // Render zone fills + outlines.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: zones.map((z) => ({
        type: 'Feature',
        id: z.id,
        properties: {
          id: z.id,
          zoneType: z.zoneType,
          fill: ZONE_TYPE_STYLE[z.zoneType]?.fill ?? '#3fe576',
          outline: ZONE_TYPE_STYLE[z.zoneType]?.outline ?? '#22d47a',
          name: z.name,
        },
        geometry: z.polygon,
      })),
    };
    const src = map.getSource('hole-zones') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(fc);
    } else {
      map.addSource('hole-zones', { type: 'geojson', data: fc });
      map.addLayer({
        id: 'hole-zones-fill',
        type: 'fill',
        source: 'hole-zones',
        paint: {
          'fill-color': ['coalesce', ['get', 'fill'], '#3fe576'],
          'fill-opacity': 0.35,
        },
      });
      map.addLayer({
        id: 'hole-zones-line',
        type: 'line',
        source: 'hole-zones',
        paint: {
          'line-color': ['coalesce', ['get', 'outline'], '#22d47a'],
          'line-width': 2,
        },
      });
    }
  }, [zones, ready]);

  // IDW heatmap overlay — one tiny point layer per grid cell, colored by VWC.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const active = overlay === 'idw';
    if (!active) {
      if (map.getLayer('hole-idw')) map.removeLayer('hole-idw');
      if (map.getSource('hole-idw')) map.removeSource('hole-idw');
      return;
    }

    const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
    for (const z of zones) {
      const cells = idwGridForZone(z, sensors, 12);
      for (const c of cells) {
        if (c.vwc == null) continue;
        features.push({
          type: 'Feature',
          properties: { color: heatmapColor(c.vwc) },
          geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
        });
      }
    }
    const data: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features,
    };
    const src = map.getSource('hole-idw') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
    } else {
      map.addSource('hole-idw', { type: 'geojson', data });
      map.addLayer(
        {
          id: 'hole-idw',
          type: 'circle',
          source: 'hole-idw',
          paint: {
            'circle-radius': 14,
            'circle-color': ['get', 'color'],
            'circle-blur': 0.6,
          },
        },
        // Draw beneath the sensor-dot layer so dots stay sharp on top.
        map.getLayer('hole-sensors') ? 'hole-sensors' : undefined,
      );
    }
  }, [overlay, zones, sensors, ready]);

  // Sensor dots — always on.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const fc: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: sensors.map((s) => {
        const vwcPct = s.latestVwc != null ? Math.round(s.latestVwc * 100) : null;
        const depthLabel = s.depthInches != null ? `${s.depthInches}"` : '';
        const label = vwcPct != null ? `${vwcPct}%${depthLabel ? ` · ${depthLabel}` : ''}` : (depthLabel || '—');
        return {
          type: 'Feature' as const,
          id: s.id,
          properties: {
            id: s.id,
            color: vwcDotColor(s.latestVwc),
            vwc: s.latestVwc,
            depth: s.depthInches,
            label,
          },
          geometry: { type: 'Point' as const, coordinates: [s.lng, s.lat] },
        };
      }),
    };
    const src = map.getSource('hole-sensors') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(fc);
    } else {
      map.addSource('hole-sensors', { type: 'geojson', data: fc });
      // Outer glow halo — bigger, faded disc so the status color is readable
      // even against a dark satellite background at small map sizes.
      map.addLayer({
        id: 'hole-sensors-halo',
        type: 'circle',
        source: 'hole-sensors',
        paint: {
          'circle-radius': 18,
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.25,
          'circle-blur': 0.6,
        },
      });
      map.addLayer({
        id: 'hole-sensors',
        type: 'circle',
        source: 'hole-sensors',
        paint: {
          'circle-radius': 10,
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#0b1014',
          'circle-stroke-width': 2,
        },
      });
      // Number-label layer so supers read the actual VWC at a glance rather
      // than decoding the color ramp. 2" and 4" probes on a single green are
      // ~5m apart — label the shallower one above its dot and the deeper
      // one below so they don't collide at the demo zoom level.
      map.addLayer({
        id: 'hole-sensors-label',
        type: 'symbol',
        source: 'hole-sensors',
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 11,
          'text-offset': [
            'match',
            ['get', 'depth'],
            2, ['literal', [0, -1.3]],
            ['literal', [0, 1.3]],
          ],
          'text-anchor': [
            'match',
            ['get', 'depth'],
            2, 'bottom',
            'top',
          ],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#f0f2f5',
          'text-halo-color': '#0b1014',
          'text-halo-width': 1.5,
        },
      });
    }
  }, [sensors, ready]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex aspect-[16/9] items-center justify-center rounded-lg border border-border bg-bg-elevated text-xs text-fg-muted">
        Map unavailable (VITE_MAPBOX_TOKEN not set)
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="aspect-[16/9] overflow-hidden rounded-lg border border-border bg-bg-elevated"
      />
      <div className="absolute left-3 top-3 flex gap-2">
        <div className="flex overflow-hidden rounded-md border border-border bg-bg-card/90 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setOverlay('idw')}
            className={`px-2.5 py-1 text-[11px] ${overlay === 'idw' ? 'bg-greeniq-500 text-[#0b1014]' : 'text-fg-secondary hover:text-fg'}`}
          >
            Rootzone moisture
          </button>
          <button
            type="button"
            onClick={() => setOverlay('none')}
            className={`border-l border-border px-2.5 py-1 text-[11px] ${overlay === 'none' ? 'bg-bg-subtle text-fg' : 'text-fg-secondary hover:text-fg'}`}
          >
            Clean
          </button>
        </div>
        <div className="flex overflow-hidden rounded-md border border-border bg-bg-card/90 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setStyleId('satellite-streets-v12')}
            className={`px-2.5 py-1 text-[11px] ${styleId === 'satellite-streets-v12' ? 'bg-greeniq-500 text-[#0b1014]' : 'text-fg-secondary hover:text-fg'}`}
          >
            Sat + labels
          </button>
          <button
            type="button"
            onClick={() => setStyleId('satellite-v9')}
            className={`border-l border-border px-2.5 py-1 text-[11px] ${styleId === 'satellite-v9' ? 'bg-greeniq-500 text-[#0b1014]' : 'text-fg-secondary hover:text-fg'}`}
          >
            Satellite
          </button>
        </div>
      </div>
      <div className="absolute bottom-3 left-3 rounded-md border border-border bg-bg-card/90 p-2 text-[10px] text-fg-secondary backdrop-blur-sm">
        <div className="mb-1 font-semibold uppercase tracking-wide text-fg-muted">Sensor VWC</div>
        <div className="flex gap-3">
          <LegendDot color="#dc2626" label="< 10%" />
          <LegendDot color="#f59e0b" label="< 13%" />
          <LegendDot color="#16a34a" label="healthy" />
          <LegendDot color="#2563eb" label="> 35%" />
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
